import moment from 'moment';

import { createStripeAccount, fetchStripeAccount, updateStripeAccount } from '../../ducks/stripeConnectAccount.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { createBookingSummary, retrieveProviderBalance } from '../../util/api';
import { getLocaleFromPath } from '../../util/locale';
import { logError } from '../../util/log';

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/StripePayoutPage/SET_INITIAL_VALUES';

export const SAVE_PAYOUT_DETAILS_REQUEST = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_REQUEST';
export const SAVE_PAYOUT_DETAILS_SUCCESS = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_SUCCESS';
export const SAVE_PAYOUT_DETAILS_ERROR = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_ERROR';

export const FETCH_PROVIDER_BALANCE_REQUEST = 'app/StripePayoutPage/FETCH_PROVIDER_BALANCE_REQUEST';
export const FETCH_PROVIDER_BALANCE_SUCCESS = 'app/StripePayoutPage/FETCH_PROVIDER_BALANCE_SUCCESS';
export const FETCH_PROVIDER_BALANCE_ERROR = 'app/StripePayoutPage/FETCH_PROVIDER_BALANCE_ERROR';

export const FETCH_BOOKING_SUMMARY_REQUEST = 'app/StripePayoutPage/FETCH_BOOKING_SUMMARY_REQUEST';
export const FETCH_BOOKING_SUMMARY_SUCCESS = 'app/StripePayoutPage/FETCH_BOOKING_SUMMARY_SUCCESS';
export const FETCH_BOOKING_SUMMARY_ERROR = 'app/StripePayoutPage/FETCH_BOOKING_SUMMARY_ERROR';

// ================ Reducer ================ //

/** @type {import('../../../types/vansite').VanSite.State.StripePayoutPage} */
const initialState = {
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  fetchPaymentsForOverviewInProgress: false,
  fetchPaymentsForOverviewError: null,
  providerBalance: null,
  fetchProviderBalanceInProgress: false,
  fetchProviderBalanceError: null,
  bookingSummeryDates: null,
  fetchBookingSummaryInProgress: false,
  fetchBookingSummaryError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SAVE_PAYOUT_DETAILS_REQUEST:
      return { ...state, payoutDetailsSaveInProgress: true };
    case SAVE_PAYOUT_DETAILS_ERROR:
      return { ...state, payoutDetailsSaveInProgress: false };
    case SAVE_PAYOUT_DETAILS_SUCCESS:
      return { ...state, payoutDetailsSaveInProgress: false, payoutDetailsSaved: true };

    case FETCH_PROVIDER_BALANCE_REQUEST:
      return { ...state, fetchProviderBalanceInProgress: true, fetchProviderBalanceError: null };
    case FETCH_PROVIDER_BALANCE_SUCCESS:
      const { bookingSummeryDates, ...rest } = payload;
      return {
        ...state,
        providerBalance: rest,
        bookingSummeryDates,
        fetchProviderBalanceInProgress: false,
      };
    case FETCH_PROVIDER_BALANCE_ERROR:
      return {
        ...state,
        fetchProviderBalanceInProgress: false,
        fetchProviderBalanceError: payload,
      };

    case FETCH_BOOKING_SUMMARY_REQUEST:
      return { ...state, fetchBookingSummaryInProgress: true, fetchBookingSummaryError: null };
    case FETCH_BOOKING_SUMMARY_SUCCESS:
      return { ...state, fetchBookingSummaryInProgress: false };
    case FETCH_BOOKING_SUMMARY_ERROR:
      return { ...state, fetchBookingSummaryInProgress: false, fetchBookingSummaryError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const savePayoutDetailsRequest = () => ({
  type: SAVE_PAYOUT_DETAILS_REQUEST,
});
export const savePayoutDetailsError = () => ({
  type: SAVE_PAYOUT_DETAILS_ERROR,
});
export const savePayoutDetailsSuccess = () => ({
  type: SAVE_PAYOUT_DETAILS_SUCCESS,
});

export const fetchProviderBalanceRequest = () => ({
  type: FETCH_PROVIDER_BALANCE_REQUEST,
});
export const fetchProviderBalanceSuccess = (balance) => ({
  type: FETCH_PROVIDER_BALANCE_SUCCESS,
  payload: balance,
});
export const fetchProviderBalanceError = (error) => ({
  type: FETCH_PROVIDER_BALANCE_ERROR,
  payload: error,
});

export const fetchBookingSummaryRequest = () => ({
  type: FETCH_BOOKING_SUMMARY_REQUEST,
});
export const fetchBookingSummarySuccess = () => ({
  type: FETCH_BOOKING_SUMMARY_SUCCESS,
});
export const fetchBookingSummaryError = (error) => ({
  type: FETCH_BOOKING_SUMMARY_ERROR,
  payload: error,
});

// ================ Thunks ================ //

export const savePayoutDetails = (values, isUpdateCall) => (dispatch) => {
  const upsertThunk = isUpdateCall ? updateStripeAccount : createStripeAccount;
  dispatch(savePayoutDetailsRequest());

  return dispatch(upsertThunk(values, { expand: true }))
    .then((response) => {
      dispatch(fetchStripeAccount());
      dispatch(savePayoutDetailsSuccess());
      return response;
    })
    .catch(() => dispatch(savePayoutDetailsError()));
};

export const fetchBookingSummary = (params, locale) => async (dispatch, getState) => {
  dispatch(fetchBookingSummaryRequest());
  const { month, year } = params;
  try {
    const response = await createBookingSummary({
      year: moment(year, 'YYYY').year(),
      month: month ? moment(month, 'MMMM').month() + 1 : null,
      lang: locale,
    });
    dispatch(fetchBookingSummarySuccess());
    const decodedString = new TextDecoder('utf-8').decode(Uint8Array.from(atob(response.html), c => c.charCodeAt(0)));
    return {
      html: decodedString,
      fileName: response.fileName,
    };
  } catch (e) {
    logError(e, 'fetch-booking-summary-failed');
    dispatch(fetchBookingSummaryError(e));
  }
};

export const fetchProviderBalance = (locale) => async (dispatch, getState) => {
  const { fetchProviderBalanceInProgress, providerBalance } = getState().StripePayoutPage;
  // Route loaders re-run on every navigation (incl. ?tab= changes); the balance
  // is expensive to compute and changes rarely, so one fetch per session suffices
  // (same guard pattern as fetchSubscription in PassSettingsPage.duck.js).
  if (fetchProviderBalanceInProgress || providerBalance) {
    return;
  }
  dispatch(fetchProviderBalanceRequest());
  try {
    const response = await retrieveProviderBalance({ lang: locale });
    dispatch(fetchProviderBalanceSuccess(response.data));
    return response;
  } catch (e) {
    logError(e, 'fetch-provider-balance-failed');
    dispatch(fetchProviderBalanceError(e));
  }
};

export const loadData = (params, search, match) => async (dispatch, getState) => {
  const locale = getLocaleFromPath(match.path);
  let currentUser = getState().user.currentUser;
  if (!currentUser) {
    await dispatch(fetchCurrentUser());
    currentUser = getState().user.currentUser;
  }
  if (currentUser && currentUser.stripeAccount) {
    await dispatch(fetchStripeAccount());
    const { stripeAccount } = currentUser;
    const stripeAccountId = stripeAccount?.attributes?.stripeAccountId;
    if (stripeAccountId) {
      dispatch(fetchProviderBalance(locale));
    }
  }
};
